import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function ReviewPrompts() {
  const qc = useQueryClient();
  const { data: prompts = [] } = useQuery({ queryKey: ["review-requests"], queryFn: () => api.get("/me/review-requests").then((r) => r.data) });
  if (prompts.length === 0) return null;
  return (
    <div className="space-y-3 mb-8" data-testid="review-prompts">
      {prompts.map((p) => <PromptCard key={p.id} prompt={p} onDone={() => qc.invalidateQueries({ queryKey: ["review-requests"] })} />)}
    </div>
  );
}

function PromptCard({ prompt, onDone }) {
  const [rating, setRating] = useState(5);
  const [quote, setQuote] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const dismiss = async () => { await api.post(`/me/review-requests/${prompt.id}/complete`); onDone(); };
  const submit = async () => {
    if (!quote.trim()) return toast.error("Please write a short note.");
    setSaving(true);
    try {
      await api.post("/testimonials/submit", { quote, rating, subject_type: "service", subject_name: prompt.subject_name });
      await api.post(`/me/review-requests/${prompt.id}/complete`);
      toast.success("Thank you! Your review is pending approval.");
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="eyw-card p-5 border-l-4 border-l-[#D98E73]" data-testid={`review-prompt-${prompt.id}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-[#1A242B]">How was your {prompt.subject_name || "session"}?</p>
          <p className="text-xs text-[#4A5D6B]">Share a quick review — it helps others find their path.</p>
        </div>
        <button onClick={dismiss} data-testid={`review-prompt-dismiss-${prompt.id}`} className="text-[#8FA89B] hover:text-[#4A5D6B]"><X size={16} /></button>
      </div>
      {!open ? (
        <button onClick={() => setOpen(true)} data-testid={`review-prompt-open-${prompt.id}`} className="eyw-btn !py-1.5 text-xs mt-3">Leave a review</button>
      ) : (
        <div className="mt-3 space-y-2">
          <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" onClick={() => setRating(n)} data-testid={`review-prompt-star-${prompt.id}-${n}`}>
              <Star size={20} className={n <= rating ? "fill-[#D98E73] text-[#D98E73]" : "text-[#D3Cabb]"} />
            </button>))}</div>
          <textarea rows={2} value={quote} onChange={(e) => setQuote(e.target.value)} data-testid={`review-prompt-text-${prompt.id}`} className="eyw-input w-full resize-none text-sm" placeholder="Tell us about your experience…" />
          <div className="flex justify-end"><button onClick={submit} disabled={saving} data-testid={`review-prompt-submit-${prompt.id}`} className="eyw-btn !py-1.5 text-xs disabled:opacity-60">{saving && <Loader2 className="animate-spin" size={13} />} Submit</button></div>
        </div>
      )}
    </div>
  );
}
